import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import moment from "moment";
import "./Reassigndoc.css";
import Icons from "../../Assets/Icons";
import { t } from "../../Utils/translate";
import { getAllDepartment } from "../../modules/AddDoctor/AddDoctorThunk";
import { getDepartmentDoctor, getDoctorTimeslots, updateAppointment } from "../../modules/RescheduleAppointment/RescheduleAppointmentThunk";
import ReassignDoctor from "../../Components/index"
import { resetUpdatedAppointment } from "../../modules/RescheduleAppointment/RescheduleAppointmentSlice";
 
const Reassigndoc = ({ showModal, setShowModal, refreshAppointments, handleCancel, appointment, restrictToSameDoctor = false }) => {
  const dispatch = useDispatch();
  // State
  const [visible, setVisible] = useState(false);
  const [depList, setDepList] = useState([]);
  const [selectedDep, setSelectedDep] = useState([]);
  const [doctorList, setDoctorList] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState({
    doctor: null,
    selectedDuration: null,
    selectedPrice: null,
    department:null,
    selectedDate:null
  });
  const [selectedSlot, setSelectedSlot] = useState()
  const [currentStep, setCurrentStep] = useState(1); // 1: Doctor, 2: Date, 3: Justification
  const today = new Date().toISOString().split("T")[0];
  const formattedDate = new Date(appointment.startTime).toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(formattedDate);
  const [doctorDurations, setDoctorDurations] = useState({});
  const appointmentTimeRef = useRef(null);
  const [justificationValue, setJustificationValue] = useState("");
  const [justificationError, setJustificationError] = useState("");
 
  // Track initial doctor selection for comparison
  const [initialDoctorSelection, setInitialDoctorSelection] = useState(null);
  // Track initial date/time selection for comparison
  const [initialDateTimeSelection, setInitialDateTimeSelection] = useState(null);
 
  // Selectors
  const { getAllDepErr, getAllDepSuccess } = useSelector((state) => state.addDoctor);
  const { getDoctorListSuccess,getDoctorListError, doctorTimeslotsSuccess, updatedAppointmentSuccess, updateAppointmentError } = useSelector((state) => state.rescheduleAppointment);
 
  // Helper function to format time
  const formatTime = (date) => moment.utc(date).format("HH:mm");

  // Derived values
  const relativeId = appointment?.relative;
  const availableSlots = doctorTimeslotsSuccess?.data?.availableSlots?.map((slot) => {
    const start = new Date(slot.start);
    const end = new Date(slot.end);

    return `${formatTime(start)} - ${formatTime(end)}`;
  }) || [];
 
  // Memoized values
  const appointmentDetails = useMemo(() => ({
    patientName: appointment?.patient?.fullName || '',
    doctorInfo: `${appointment?.doctor?.name || ''} | ${appointment?.department?.departmentName || ''}`,
    dateTime: appointment?.createdAt ? new Date(appointment.createdAt).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }) : '',
    consultationFee: appointment?.payment ? 
      `${appointment.payment.currencySymbol} ${appointment.payment.consultationFee}` : ''
  }), [appointment]);
 
  // Check if doctor selection has changed from initial
  const hasDoctorSelectionChanged = useMemo(() => {
    if (!initialDoctorSelection || !selectedDoctor.doctor) {
      return false;
    }
 
    const hasDoctorChanged = initialDoctorSelection.doctorId !== selectedDoctor.doctor._id;
    const hasDurationChanged = initialDoctorSelection.duration !== selectedDoctor.selectedDuration?.duration;
    const hasDepartmentChanged = initialDoctorSelection.departmentId !== selectedDep?.value;
 
    return hasDoctorChanged || hasDurationChanged || hasDepartmentChanged;
  }, [initialDoctorSelection, selectedDoctor, selectedDep]);
 
  // Check if date/time selection has changed from initial
  const hasDateTimeSelectionChanged = useMemo(() => {
    if (!initialDateTimeSelection) {
      return false;
    }
 
    const hasDateChanged = initialDateTimeSelection.date !== selectedDate;
    const hasDurationChanged = initialDateTimeSelection.duration !== selectedDoctor.selectedDuration?.duration;
    const hasTimeChanged = initialDateTimeSelection.timeSlot !== selectedSlot;
 
    return hasDateChanged || hasDurationChanged || hasTimeChanged;
  }, [initialDateTimeSelection, selectedDate, selectedDoctor.selectedDuration, selectedSlot]);
 
  // Check if we can proceed to next step
  const canProceedToDateSelection = selectedDoctor.doctor && selectedDoctor.selectedDuration;
  const canProceedToJustification = selectedDoctor.doctor && selectedDate && selectedSlot && hasDateTimeSelectionChanged;
 
  // Effects
  useEffect(() => {
    dispatch(getAllDepartment());
  }, [dispatch]);
 
  useEffect(() => {
    if (selectedDep) {
      const departmentId = selectedDep.value;
      if (departmentId && relativeId) {
        dispatch(getDepartmentDoctor({ relativeId, departmentId }));
      }
    }
  }, [selectedDep]);
 
  // Initialize durations when doctorList changes
  useEffect(() => {
    if (doctorList.length > 0) {
      const initialDurations = {};
      doctorList.forEach(doctor => {
        if (doctor.availableDurationPrices?.length > 0) {
          initialDurations[doctor._id] = doctor.availableDurationPrices[0];
        }
      });
      setDoctorDurations(initialDurations);
    }
  }, [doctorList]);
 
  useEffect(() => {
    if (doctorList.length && appointment?.doctor?._id) {
      const matchedDoctor = doctorList.find(
        (doc) => doc._id === appointment.doctor._id
      );
 
      if (matchedDoctor) {
        const defaultDuration = matchedDoctor.availableDurationPrices?.[0];
        const newSelectedDoctor = {
          doctor: matchedDoctor,
          selectedDuration: defaultDuration,
          selectedPrice: defaultDuration?.price,
          department: selectedDep,
          selectedDate: selectedDate
        };
 
        setSelectedDoctor(newSelectedDoctor);
 
        // Set initial doctor selection for comparison (only once)
        if (!initialDoctorSelection) {
          setInitialDoctorSelection({
            doctorId: matchedDoctor._id,
            duration: defaultDuration?.duration,
            departmentId: selectedDep?.value,
            price: defaultDuration?.price
          });
        }
 
        // Set initial date/time selection for comparison (only once)
        if (!initialDateTimeSelection) {
          // Extract original time from appointment
          const originalStartTime = new Date(appointment.startTime);
          const originalEndTime = new Date(originalStartTime.getTime() + appointment.duration * 60000);
          const originalTimeSlot = `${formatTime(originalStartTime)} - ${formatTime(originalEndTime)}`;

          setInitialDateTimeSelection({
            date: formattedDate,
            duration: appointment.duration,
            timeSlot: originalTimeSlot
          });
        }
      }
    }
  }, [doctorList, appointment, selectedDep]);
 
  useEffect(() => {
    if(getAllDepErr){
      return toast.error(getAllDepErr);
    }
    if (getAllDepSuccess?.status === "success" && getAllDepSuccess?.data?.departments) {
      const transformedData = getAllDepSuccess.data.departments.map((dept) => ({
        value: dept._id,
        label: dept.departmentName,
        code: dept.code,
        duration: dept.duration
      }));
      setDepList(transformedData);
    }
  }, [getAllDepSuccess, getAllDepErr]);
 
  useEffect(()=>{
    if(updateAppointmentError){
       console.error("Update appointment error from state:", updateAppointmentError);
       toast.error(updateAppointmentError)
    }
    if(updatedAppointmentSuccess?.status === "success"){
      toast.success(t("updateAppointment"))
       setShowModal(false)
       refreshAppointments();
    }
    dispatch( resetUpdatedAppointment())
  },[updatedAppointmentSuccess, updateAppointmentError])
 
 
  useEffect(() => {
    if (appointment?.department?.departmentName && depList.length > 0) {
      const matchedDep = depList.find(
        (dep) => dep.label === appointment.department.departmentName
      );
      if (matchedDep) {
        setSelectedDep(matchedDep);
 
        // Also update initial selection if not set
        if (!initialDoctorSelection) {
          setInitialDoctorSelection(prev => prev ? {
            ...prev,
            departmentId: matchedDep.value
          } : null);
        }
      }
    }
  }, [appointment, depList]);
 
  useEffect(() => {
    if (restrictToSameDoctor && selectedDoctor.doctor && selectedDoctor.selectedDuration && currentStep === 1 && selectedDep) {
      if (!selectedDoctor.department) {
        setSelectedDoctor(prev => ({ ...prev, department: selectedDep }));
      }
      dispatch(getDoctorTimeslots({
        doctorId: selectedDoctor.doctor._id,
        date: today,
        duration: selectedDoctor.selectedDuration.duration
      }));
      setCurrentStep(2);
    }
  }, [restrictToSameDoctor, selectedDoctor, currentStep, dispatch, today, selectedDep]);
 
  useEffect(() => {
    if(getDoctorListError){
      return toast.error(getDoctorListError)
    }
    if (getDoctorListSuccess?.status === "success" && getDoctorListSuccess?.data?.doctors) {
      let doctors = getDoctorListSuccess?.data?.doctors;
      if (restrictToSameDoctor && appointment?.doctor?._id) {
        doctors = doctors.filter(doctor => doctor._id === appointment.doctor._id);
      }
      setDoctorList(doctors);
    }
  }, [getDoctorListSuccess, getDoctorListError, restrictToSameDoctor, appointment]);
 
 
  useEffect(() => {
    setVisible(showModal);
  }, [showModal]);
 
  // Handlers
  const handleCloseWithTransition = useCallback((callback) => {
    setVisible(false);
    setTimeout(() => {
      callback();
    }, 300);
  }, []);
 
  const handleDropdownChange = useCallback((department) => {
    if (!restrictToSameDoctor) {
      setSelectedDep(department);
    }
  }, [restrictToSameDoctor]);
 
  const handleDoctorDurationChange = useCallback((doctorId, durationObj) => {
    setDoctorDurations(prev => ({
      ...prev,
      [doctorId]: durationObj
    }));
 
    // Update selected doctor if this is the currently selected doctor
    if (selectedDoctor.doctor?._id === doctorId) {
      setSelectedDoctor(prev => ({
        ...prev,
        selectedDuration: durationObj,
        selectedPrice: durationObj.price,
        department: selectedDep,
        selectedDate: selectedDate
      }));
    }
  }, [selectedDoctor, selectedDep, selectedDate]);
 
  const handleJustificationChange = (value) => {
     if (justificationError && value.length >= 10) {
      setJustificationError("");
    }else{
        setJustificationValue(value);
    }
  };
 
  const handleDoctorSelection = useCallback((doctor) => {
    const currentDuration = doctorDurations[doctor._id] || doctor.availableDurationPrices?.[0];
    setSelectedDoctor({
      doctor: doctor,
      selectedDuration: currentDuration,
      selectedPrice: currentDuration?.price,
      department: selectedDep,
      selectedDate: selectedDate
    });
  }, [doctorDurations, selectedDep, selectedDate]);
 
  const getDoctorPrice = useCallback((doctorId) => {
    const duration = doctorDurations[doctorId];
    return duration?.price || doctorList.find(doc => doc._id === doctorId)?.availableDurationPrices?.[0]?.price || 'N/A';
  }, [doctorDurations, doctorList]);
 
  const handleDateChange = useCallback((e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
  }, []);
 
  const handleContinue = useCallback(() => {
    console.log(currentStep, canProceedToDateSelection, "sanjeev")
    if (currentStep === 1) {
      // Get timeslots for selected doctor
      dispatch(getDoctorTimeslots({
        doctorId: selectedDoctor.doctor._id,
        date: today,
        duration: selectedDoctor.selectedDuration.duration
      }));
      setCurrentStep(2);
    } else if (currentStep === 2 && canProceedToJustification) {
       if (justificationValue.trim().length < 10) {
        setJustificationError(t("justificationErr"));
        return; 
      }
      setJustificationError("");
      setCurrentStep(3);
      setSelectedDoctor(prev => ({
        ...prev,
        selectedDate: selectedDate,
        selectedSlot: selectedSlot,
        department: prev.department || selectedDep
      }));
    } else if (currentStep === 3) {
      // Handle final submission
      const slot = selectedSlot || selectedDoctor?.selectedSlot;
      const dateToUse = selectedDate || selectedDoctor?.selectedDate || formattedDate;
      if (!slot || !dateToUse) {
        toast.error(t("pleaseSelectTimeSlot") || "Please select a time slot and date");
        return;
      }
      if (!selectedDoctor?.doctor?._id) {
        toast.error(t("pleaseSelectDoctor") || "Please select a doctor");
        return;
      }
      if (!selectedDoctor?.selectedDuration?.duration) {
        toast.error(t("pleaseSelectDuration") || "Please select a duration");
        return;
      }
      const startTime = slot.split(" - ")[0];
      // const finalDate = dateToUse + " " + startTime;
      // const convertedDate = moment.utc(finalDate, "YYYY-MM-DD HH:mm").format();
      const finalDate= selectedDoctor.selectedDate + " " + startTime
      const convertedDate = moment(finalDate, "YYYY-MM-DD HH:mm").utc().format();
      const departmentId = selectedDoctor.department?.value || selectedDep?.value || appointment?.department?._id;
      if (!departmentId) {
        toast.error(t("departmentRequired") || "Department is required");
        return;
      }
      const data = {
        doctorId: selectedDoctor.doctor._id,
        departmentId: departmentId,
        startTime: convertedDate,
        duration: selectedDoctor.selectedDuration.duration,
        reschedulingReason: justificationValue,
      };
      console.log("Submitting appointment update:", { appointmentId: appointment._id, data });
      if (data.doctorId && data.departmentId && data.startTime && data.duration && data.reschedulingReason && appointment._id) {
        dispatch(updateAppointment({appointmentId: appointment._id, data}));
      } else {
        console.error("Validation failed:", { doctorId: data.doctorId, departmentId: data.departmentId, startTime: data.startTime, duration: data.duration, reschedulingReason: data.reschedulingReason, appointmentId: appointment._id });
        toast.error(t("missingRequiredFields") || "Missing required fields");
      }
    }
  }, [currentStep, selectedDoctor, selectedDate, selectedSlot, justificationValue, canProceedToDateSelection, canProceedToJustification, dispatch, today, formattedDate, selectedDep, appointment, t]);
 
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
 
  const handleSlotClick = (slot) => {
    setSelectedSlot(slot); 
    if (appointmentTimeRef.current) {
      appointmentTimeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
 
  // Render helpers
  const renderAppointmentSummary = () => (
    <div className="rr-body-og-sum">
      <div className="rr-og-pnts">
        <b>{t("pName")}:</b> { appointmentDetails.patientName}
      </div>
      <div>
        <b>{t("docDep")}:</b> {currentStep === 3 ?  `${selectedDoctor?.doctor?.name} | ${selectedDoctor.department.label}`  :appointmentDetails.doctorInfo}
      </div>
      <div>
        <b>{t("dataTime")}:</b> {currentStep === 3 ? `${selectedDoctor.selectedDate} | ${selectedDoctor.selectedSlot}` : appointmentDetails.dateTime}
      </div>
      <div>
        <b>{t("consFee")}:</b> 
        <span className="h-fit relative left-[1px] top-[4px] text-[25px]">
          {appointment.payment.currencySymbol}
        </span> {" "}
        {currentStep === 3 ? selectedDoctor.selectedPrice : appointment.payment.consultationFee}
      </div>
    </div>
  );
 
  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return t("selDoc");
      case 2:
        return t("selDate");
      case 3:
        return ""; // You can add translation for this
      default:
        return t("selDoc");
    }
  };
 
  const getContinueButtonText = () => {
    switch (currentStep) {
      case 1:
        return t("btnContinue");
      case 2:
        return t("btnContinue");
      case 3:
        return "Confirm Reassignment"; // You can add translation for this
      default:
        return t("btnContinue");
    }
  };
 
  const isContinueDisabled = () => {
    switch (currentStep) {
      case 1:
        return !canProceedToDateSelection;
      case 2:
        return !canProceedToJustification;
      case 3:
        return !justificationValue.trim() || justificationValue.trim().length < 10;
      default:
        return true;
    }
  };
 
  return (
    <div className="rr-fixed">
      <div className={`rr-container ${visible ? "modal-open" : "modal-close"}`}>
        <div className="rr-header">
          {t("rrApp")}
          <img 
            src={Icons.cross} 
            className="cross-icon-modal" 
            onClick={() => handleCloseWithTransition(handleCancel)} 
            alt="Close"
          />
        </div>
 
        <div className="rr-body">
          <div className="rr-body-head">{currentStep === 3 ? t("Appointment update summary") : t("ogAppSum")}</div>
          {renderAppointmentSummary()}
 
          <div className="rr-body-head">
            {getStepTitle()}
          </div>
 
          {currentStep === 1 && (
            <ReassignDoctor.DoctorSelection
              depList={depList}
              selectedDep={selectedDep}
              doctorList={doctorList}
              selectedDoctor={selectedDoctor}
              doctorDurations={doctorDurations}
              onDropdownChange={handleDropdownChange}
              onDoctorSelection={handleDoctorSelection}
              onDoctorDurationChange={handleDoctorDurationChange}
              getDoctorPrice={getDoctorPrice}
              hasChanges={hasDoctorSelectionChanged}
              hideDepartmentDropdown={restrictToSameDoctor}
            />
          )}
 
          {currentStep === 2 && (
            <ReassignDoctor.DateSelection
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              availableSlots={availableSlots}
              appointmentTimeRef={appointmentTimeRef}
              onDateChange={handleDateChange}
              onSlotClick={handleSlotClick}
              today={today}
              onJustificationChange={handleJustificationChange}
              justificationValue={justificationValue}
              justificationError={justificationError}
              hasDateTimeChanges={hasDateTimeSelectionChanged}
            />
          )}
 
          <div className="action-buttons">
            <button 
              className="btn-rs btn-rs-cancel" 
              onClick={currentStep === 1 ? () => handleCloseWithTransition(handleCancel) : handleBack}
            >
              {currentStep === 1 ? t("btnCancel") : t("Back")}
            </button>
            <button 
               className={`btn-rs btn-rs-continue ${isContinueDisabled() ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`} 
              onClick={handleContinue}
              disabled={isContinueDisabled()}
            >
              {getContinueButtonText()}
            </button>
          </div>
        </div>
 
      </div>
    </div>
  );
};
 
export default Reassigndoc;